import {
  DEFAULT_LANGUAGE,
  localization,
  SupportedLanguage,
  useAppSelector,
  useLanguage,
  useLocalization,
} from "@next-platform/client-common-core";
import { useEffect, useRef, useState } from "react";

import appLogger from "@next-platform/client-common-core/logger/appLogger";

import useIsMultiLangEnabled from "./useIsMultiLangEnabled";

const localizationGateLogger = appLogger.withTag("LocalizationGate");

const resetLocalizationToDefault = async ({ currentLanguage }: { currentLanguage: string }): Promise<void> => {
  if (currentLanguage !== DEFAULT_LANGUAGE) {
    await localization.setCurrentLanguage(SupportedLanguage.EN);
  }

  const loadedLanguages = localization.getLoadedLanguages();
  await Promise.all(
    loadedLanguages
      .filter((loadedLanguage: string) => loadedLanguage !== DEFAULT_LANGUAGE)
      .map((loadedLanguage: string) => localization.removeLanguageResources({ language: loadedLanguage as SupportedLanguage })),
  );
};

const useLocalizationGate = () => {
  const { isInitialized } = useLocalization();
  const { isMultiLangEnabled } = useIsMultiLangEnabled();
  const { currentLanguage } = useLanguage();
  const appLocalization = useAppSelector((state) => state?.app?.appSettings?.localization);
  const [stateIsReady, setStateIsReady] = useState(false);
  const refResetTriggered = useRef(false);
  const refAppConfigSent = useRef(false);

  useEffect(() => {
    if (!isInitialized) return;

    if (isMultiLangEnabled) {
      setStateIsReady(true);
      return;
    }

    if (refResetTriggered.current) return;
    refResetTriggered.current = true;

    resetLocalizationToDefault({ currentLanguage })
      .then(() => setStateIsReady(true))
      .catch((resetError: unknown) => {
        localizationGateLogger.error("Failed to reset language to English:", resetError);
        setStateIsReady(true);
      });
  }, [isInitialized, isMultiLangEnabled, currentLanguage]);

  useEffect(() => {
    if (!stateIsReady || refAppConfigSent.current) return;
    if (!appLocalization) return;

    refAppConfigSent.current = true;
    void localization.setAppConfig(appLocalization);
  }, [stateIsReady, appLocalization]);

  return { isLocalizationReady: stateIsReady };
};

export default useLocalizationGate;
